import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import TouchAppIcon from '@mui/icons-material/TouchApp'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ContactPhoneIcon from '@mui/icons-material/ContactPhone'
import { DateRangePicker, MetricCard } from '../../components/Performance'
import StatTable, { StatColumn } from '../../components/Performance/StatTable'
import SeriesChart from '../../components/Performance/SeriesChart'
import { supabase } from '../../config/supabase'
import {
  BreakdownRow,
  DEVICE_LABELS,
  EVENT_LABELS,
  PAGE_TYPE_LABELS,
  StatsEntity,
  StatsScope,
  avgPosition,
  contactsOf,
  fmt,
  fmtPct,
  periodFor,
  rate,
  refLabel,
  statsErrorMessage,
  sumSeries,
  trend,
  useAreaBenchmark,
  useStatsBreakdown,
  useStatsEntities,
  useStatsSeries,
} from '../../hooks/useStats'

interface Branch {
  id: string
  name: string
  city: string | null
}

const pos = (v: number | null) => (v === null ? '-' : v.toLocaleString('da-DK', { maximumFractionDigits: 1 }))

// Columns shared by the tables that show how a listing performs
const listingColumns: StatColumn<BreakdownRow>[] = [
  {
    key: 'impressions',
    label: 'Vist i lister',
    help: 'Antal gange kortet var mindst halvt synligt i et sekund i en liste',
    value: (r) => r.impressions,
  },
  { key: 'profile_clicks', label: 'Klik på profil', value: (r) => r.profile_clicks },
  {
    key: 'ctr',
    label: 'Klikrate',
    help: 'Klik på profil delt med visninger i lister',
    value: (r) => rate(r.profile_clicks, r.impressions),
    format: (v) => fmtPct(v),
  },
  {
    key: 'position',
    label: 'Ø placering',
    help: 'Gennemsnitlig placering i listen, hvor 1 er øverst',
    value: (r) => avgPosition(r),
    format: (v) => pos(v),
  },
  { key: 'contacts', label: 'Kontaktklik', value: (r) => r.contacts },
]

export default function PerformanceStats() {
  const { entities, loading: entitiesLoading, isAdmin } = useStatsEntities()
  const [params, setParams] = useSearchParams()
  const [days, setDays] = useState(30)
  const [branches, setBranches] = useState<Branch[]>([])
  const [placeMode, setPlaceMode] = useState<'place' | 'search_place'>('place')

  const selectedId = params.get('id')
  const branchId = params.get('branch') || 'all'
  const entity: StatsEntity | null = useMemo(
    () => entities.find((e) => e.id === selectedId) ?? entities[0] ?? null,
    [entities, selectedId]
  )

  // Branches of a chain
  useEffect(() => {
    if (entity?.type !== 'chain') {
      setBranches([])
      return
    }
    let cancelled = false
    supabase
      .from('undertakers')
      .select('id, name, city')
      .eq('chain_id', entity.id)
      .order('name')
      .then(({ data }) => {
        if (!cancelled) setBranches(data || [])
      })
    return () => {
      cancelled = true
    }
  }, [entity])

  const scope: StatsScope = useMemo(() => {
    if (!entity) return {}
    if (entity.type === 'undertaker') return { undertakerId: entity.id }
    return branchId !== 'all' ? { undertakerId: branchId, chainId: entity.id } : { chainId: entity.id }
  }, [entity, branchId])

  const period = periodFor(days)
  const ready = !!entity
  // One call covers this period and the one before, for the trend arrows
  const series = useStatsSeries(period.previousFrom, period.to, scope, ready)
  const places = useStatsBreakdown(period.from, period.to, placeMode, scope, 20, ready)
  const pageTypes = useStatsBreakdown(period.from, period.to, 'page_type', scope, 20, ready)
  const events = useStatsBreakdown(period.from, period.to, 'event', scope, 20, ready)
  const refs = useStatsBreakdown(period.from, period.to, 'ref', scope, 20, ready)
  const devices = useStatsBreakdown(period.from, period.to, 'device', scope, 10, ready)
  const area = useAreaBenchmark(period.from, period.to, scope, ready)
  const isChainAll = entity?.type === 'chain' && branchId === 'all'
  const branchRows = useStatsBreakdown(period.from, period.to, 'branch', scope, 200, ready && isChainAll)

  const rows = series.data || []
  const current = rows.filter((r) => r.day >= period.from)
  const previous = rows.filter((r) => r.day <= period.previousTo)
  const now = sumSeries(current)
  const before = sumSeries(previous)
  const contactsNow = contactsOf(now)
  const contactsBefore = contactsOf(before)
  const error = series.error || places.error || events.error

  const select = (id: string | null, branch?: string) => {
    const next = new URLSearchParams(params)
    if (id) next.set('id', id)
    else next.delete('id')
    if (branch && branch !== 'all') next.set('branch', branch)
    else next.delete('branch')
    setParams(next, { replace: true })
  }

  if (entitiesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!entity) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {isAdmin
          ? 'Ingen profiler fundet.'
          : 'Du har ingen godkendte forretninger endnu. Anmod om at overtage en profil under "Mine forretninger" for at se statistik.'}
      </Alert>
    )
  }

  // Clicks by type and where they happened
  const eventMap = new Map((events.data || []).map((r) => [r.key, r.events]))
  const clickRows = Object.keys(EVENT_LABELS).map((type) => {
    const listing = eventMap.get(`${type}:listing`) ?? 0
    const profile = eventMap.get(`${type}:profile`) ?? 0
    return { type, label: EVENT_LABELS[type], listing, profile, total: listing + profile }
  })

  const branchName = new Map(branches.map((b) => [b.id, b]))
  const branchProfileViews = (branchRows.data || []).reduce((s, r) => s + r.profile_views, 0)
  const chainPageViews = Math.max(0, now.profile_views - branchProfileViews)

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 280, flex: '0 1 360px' }}
            options={entities}
            value={entity}
            groupBy={(o) => (o.type === 'chain' ? 'Kæder' : 'Bedemænd')}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) => select(v?.id ?? null)}
            disableClearable
            renderInput={(p) => <TextField {...p} label={isAdmin ? 'Vælg profil' : 'Vælg din profil'} />}
          />
          {entity.type === 'chain' && branches.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Afdeling</InputLabel>
              <Select label="Afdeling" value={branchId} onChange={(e) => select(entity.id, String(e.target.value))}>
                <MenuItem value="all">Hele kæden</MenuItem>
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                    {b.city ? `, ${b.city}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <DateRangePicker value={days} onChange={setDays} />
      </Box>

      {error ? (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          {statsErrorMessage(error)}
        </Alert>
      ) : null}

      {series.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Key figures */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
            <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <MetricCard
                title="Vist i lister"
                value={now.impressions}
                trend={trend(now.impressions, before.impressions)}
                trendLabel="vs. forrige periode"
                subtitle={avgPosition(now) !== null ? `Ø placering ${pos(avgPosition(now))}` : undefined}
                icon={<FormatListBulletedIcon sx={{ fontSize: 20 }} />}
                color="secondary"
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <MetricCard
                title="Klik på profil"
                value={now.profile_clicks}
                trend={trend(now.profile_clicks, before.profile_clicks)}
                trendLabel="vs. forrige periode"
                subtitle={`Klikrate ${fmtPct(rate(now.profile_clicks, now.impressions))}`}
                icon={<TouchAppIcon sx={{ fontSize: 20 }} />}
                color="info"
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <MetricCard
                title="Profilvisninger"
                value={now.profile_views}
                trend={trend(now.profile_views, before.profile_views)}
                trendLabel="vs. forrige periode"
                subtitle={`ca. ${fmt(now.profile_view_sessions)} besøgende`}
                icon={<VisibilityIcon sx={{ fontSize: 20 }} />}
                color="primary"
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <MetricCard
                title="Kontaktklik"
                value={contactsNow}
                trend={trend(contactsNow, contactsBefore)}
                trendLabel="vs. forrige periode"
                subtitle={`${fmtPct(rate(contactsNow - now.listing_contacts, now.profile_views))} af profilvisninger, ${fmt(now.listing_contacts)} direkte fra lister`}
                icon={<ContactPhoneIcon sx={{ fontSize: 20 }} />}
                color="success"
              />
            </Box>
          </Box>

          {/* Trend */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <SeriesChart
                title="Udvikling dag for dag"
                rows={current}
                metrics={[
                  { key: 'impressions', label: 'Vist i lister', color: '#7c3aed', value: (r) => r.impressions },
                  { key: 'profile_clicks', label: 'Klik på profil', color: '#0284c7', value: (r) => r.profile_clicks },
                  { key: 'profile_views', label: 'Profilvisninger', color: '#5c4b99', value: (r) => r.profile_views },
                  { key: 'contacts', label: 'Kontaktklik', color: '#16a34a', value: (r) => contactsOf(r) },
                ]}
              />
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
            {/* Clicks by type */}
            <Card sx={{ flex: '1 1 420px', minWidth: 300 }}>
              <CardContent>
                <StatTable
                  title="Klik fordelt på type"
                  subtitle="Lister er lokationssider, søgninger og guides. Profil er jeres egen side."
                  rows={clickRows}
                  rowKey={(r) => r.type}
                  loading={events.isLoading}
                  columns={[
                    { key: 'label', label: 'Klik', align: 'left', value: (r) => r.label },
                    { key: 'listing', label: 'Fra lister', value: (r) => r.listing },
                    { key: 'profile', label: 'Fra profil', value: (r) => r.profile },
                    { key: 'total', label: 'I alt', value: (r) => r.total },
                  ]}
                />
              </CardContent>
            </Card>

            {/* Where on the site */}
            <Card sx={{ flex: '1 1 420px', minWidth: 300 }}>
              <CardContent>
                <StatTable
                  title="Hvor på sitet"
                  rows={(pageTypes.data || []).filter((r) => r.events > 0)}
                  rowKey={(r) => r.key}
                  loading={pageTypes.isLoading}
                  initialSort="impressions"
                  columns={[
                    { key: 'label', label: 'Sidetype', align: 'left', value: (r) => PAGE_TYPE_LABELS[r.key] ?? r.key },
                    ...listingColumns.filter((c) => c.key !== 'position'),
                    { key: 'profile_views', label: 'Profilvisninger', value: (r) => r.profile_views },
                  ]}
                />
              </CardContent>
            </Card>
          </Box>

          {/* Locations */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Hvor I blev vist
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {placeMode === 'place'
                      ? 'Lokationssider og adressesøgninger. En adressesøgning tælles på den nærmeste by, aldrig på adressen.'
                      : 'Kun adressesøgninger, samlet på den nærmeste by.'}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={placeMode}
                  onChange={(_, v) => v && setPlaceMode(v)}
                  sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }}
                >
                  <ToggleButton value="place">Alle steder</ToggleButton>
                  <ToggleButton value="search_place">Kun søgninger</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <StatTable
                rows={(places.data || []).filter((r) => r.impressions > 0 || r.profile_clicks > 0)}
                rowKey={(r) => r.key}
                loading={places.isLoading}
                initialSort="impressions"
                columns={[{ key: 'label', label: 'By eller område', align: 'left', value: (r) => r.label }, ...listingColumns]}
              />
            </CardContent>
          </Card>

          {/* Area comparison */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <StatTable
                title="Sammenlignet med andre i området"
                subtitle="Per by: jeres tal ved siden af den typiske bedemand, der blev vist på de samme sider (medianen). Andre bedemænd nævnes aldrig, og typiske tal vises kun, når mindst 3 andre blev vist. Kontaktklik tæller her kun klik direkte fra listen."
                rows={area.data || []}
                rowKey={(r) => r.key}
                loading={area.isLoading}
                initialSort="impressions"
                columns={[
                  { key: 'label', label: 'By eller område', align: 'left', value: (r) => r.label },
                  { key: 'impressions', label: 'Vist i lister', value: (r) => r.impressions },
                  {
                    key: 'peer_impressions',
                    label: 'Typisk',
                    help: 'Medianen for de andre bedemænd, der blev vist på de samme sider',
                    value: (r) => r.peer_impressions,
                    format: (v) => pos(v),
                  },
                  { key: 'ctr', label: 'Klikrate', value: (r) => rate(r.profile_clicks, r.impressions), format: (v) => fmtPct(v) },
                  {
                    key: 'peer_ctr',
                    label: 'Typisk klikrate',
                    value: (r) => (r.peer_ctr === null ? null : r.peer_ctr * 100),
                    format: (v) => fmtPct(v),
                  },
                  { key: 'contacts', label: 'Kontaktklik', value: (r) => r.contacts },
                  { key: 'peer_contacts', label: 'Typisk', value: (r) => r.peer_contacts, format: (v) => pos(v) },
                  {
                    key: 'rank',
                    label: 'Nr. i kontaktklik',
                    help: 'Jeres plads blandt alle, der blev vist i byen, efter kontaktklik. Står flere lige, får de samme plads.',
                    value: (r) => r.rank_contacts,
                    format: (v, r) => (v === null ? '-' : `${v} af ${r.peers + 1}`),
                  },
                ]}
              />
            </CardContent>
          </Card>

          {/* Branches of a chain */}
          {isChainAll && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <StatTable
                  title="Afdelinger"
                  subtitle={`Tryk på en afdeling for at se den alene. Kædesiden selv havde ${fmt(chainPageViews)} profilvisninger.`}
                  rows={branchRows.data || []}
                  rowKey={(r) => r.key}
                  loading={branchRows.isLoading}
                  initialSort="impressions"
                  onRowClick={(r) => select(entity.id, r.key)}
                  columns={[
                    {
                      key: 'label',
                      label: 'Afdeling',
                      align: 'left',
                      value: (r) => {
                        const b = branchName.get(r.key)
                        return b ? `${b.name}${b.city ? `, ${b.city}` : ''}` : r.label
                      },
                    },
                    ...listingColumns,
                    { key: 'profile_views', label: 'Profilvisninger', value: (r) => r.profile_views },
                  ]}
                />
              </CardContent>
            </Card>
          )}

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {/* Sources */}
            <Card sx={{ flex: '1 1 420px', minWidth: 300 }}>
              <CardContent>
                <StatTable
                  title="Hvor kom de besøgende fra"
                  subtitle="Siden før profilen, eller hjemmesiden de kom fra."
                  rows={(refs.data || []).filter((r) => r.profile_views > 0)}
                  rowKey={(r) => r.key}
                  loading={refs.isLoading}
                  initialSort="profile_views"
                  columns={[
                    { key: 'label', label: 'Kilde', align: 'left', value: (r) => refLabel(r.key) },
                    { key: 'profile_views', label: 'Profilvisninger', value: (r) => r.profile_views },
                    {
                      key: 'share',
                      label: 'Andel',
                      value: (r) => rate(r.profile_views, now.profile_views),
                      format: (v) => fmtPct(v, 0),
                    },
                  ]}
                />
              </CardContent>
            </Card>

            {/* Devices */}
            <Card sx={{ flex: '1 1 300px', minWidth: 280 }}>
              <CardContent>
                <StatTable
                  title="Enheder"
                  rows={(devices.data || []).filter((r) => r.events > 0)}
                  rowKey={(r) => r.key}
                  loading={devices.isLoading}
                  initialSort="impressions"
                  columns={[
                    { key: 'label', label: 'Enhed', align: 'left', value: (r) => DEVICE_LABELS[r.key] ?? r.key },
                    { key: 'impressions', label: 'Vist i lister', value: (r) => r.impressions },
                    { key: 'profile_views', label: 'Profilvisninger', value: (r) => r.profile_views },
                    { key: 'contacts', label: 'Kontaktklik', value: (r) => r.contacts },
                  ]}
                />
              </CardContent>
            </Card>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            Tallene tælles uden cookies, så "besøgende" er et skøn: en genindlæst side tæller som et nyt besøg.
            Et opkald fra et nummer, man har læst på skærmen, kan ikke måles. Kun klik på nummeret tæller.
          </Typography>
        </>
      )}
    </Box>
  )
}
